import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add, ExpandMore, Edit, Delete } from '@mui/icons-material';
import axios from 'axios';

interface Permission {
  id: string;
  resource: string;
  actions: string[];
  scope: string;
  description: string;
  is_system: boolean;
  is_active: boolean;
}

interface Role {
  id: string;
  name: string;
  display_name: string;
  level: number;
  permission_ids: string[];
  is_system: boolean;
}

interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
}

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function RBACManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [userRoles, setUserRoles] = useState<Record<string, string>>({});
  const [permissionDialog, setPermissionDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [assignRoleDialog, setAssignRoleDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [newPermission, setNewPermission] = useState({
    resource: '',
    actions: [] as string[],
    scope: 'all',
    description: '',
  });
  const [newRole, setNewRole] = useState({
    name: '',
    display_name: '',
    level: 1,
    permission_ids: [] as string[],
  });

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    loadPermissions();
    loadRoles();
    loadAdminUsers();
  }, []);

  const loadPermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/settings/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPermissions(response.data);
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  };

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/settings/roles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[RBAC] Loaded roles:', response.data);
      console.log('[RBAC] First role details:', JSON.stringify(response.data[0], null, 2));
      setRoles(response.data);
    } catch (error) {
      console.error('Failed to load roles:', error);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/admin/admin-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdminUsers(response.data);
      
      // Load role assignments for each user
      const roleMap: Record<string, string> = {};
      for (const user of response.data) {
        try {
          const roleResponse = await axios.get(
            `${API_URL}/api/v1/settings/users/${user.id}/roles`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          roleMap[user.id] = roleResponse.data.role_id || '';
        } catch (err) {
          roleMap[user.id] = '';
        }
      }
      setUserRoles(roleMap);
    } catch (error) {
      console.error('Failed to load admin users:', error);
    }
  };

  const handleCreatePermission = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingPermission) {
        // Update existing permission
        await axios.put(
          `${API_URL}/api/v1/settings/permissions/${editingPermission.id}`,
          newPermission,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new permission
        await axios.post(
          `${API_URL}/api/v1/settings/permissions`,
          newPermission,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      await loadPermissions();
      setPermissionDialog(false);
      setEditingPermission(null);
      setNewPermission({ resource: '', actions: [], scope: 'all', description: '' });
    } catch (error: any) {
      console.error('Failed to save permission:', error);
      alert(error.response?.data?.detail || 'Failed to save permission');
    }
  };

  const handleEditPermission = (permission: Permission) => {
    setEditingPermission(permission);
    setNewPermission({
      resource: permission.resource,
      actions: permission.actions,
      scope: permission.scope,
      description: permission.description,
    });
    setPermissionDialog(true);
  };

  const handleDeletePermission = async (permissionId: string) => {
    if (!confirm('Are you sure you want to delete this permission?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/v1/settings/permissions/${permissionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadPermissions();
    } catch (error: any) {
      console.error('Failed to delete permission:', error);
      alert(error.response?.data?.detail || 'Failed to delete permission');
    }
  };

  const handleCreateRole = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (editingRole) {
        // Update existing role
        await axios.put(
          `${API_URL}/api/v1/settings/roles/${editingRole.id}`,
          newRole,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new role
        await axios.post(
          `${API_URL}/api/v1/settings/roles`,
          newRole,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      await loadRoles();
      setRoleDialog(false);
      setEditingRole(null);
      setNewRole({ name: '', display_name: '', level: 1, permission_ids: [] });
    } catch (error: any) {
      console.error('Failed to save role:', error);
      alert(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setNewRole({
      name: role.name,
      display_name: role.display_name,
      level: role.level,
      permission_ids: role.permission_ids,
    });
    setRoleDialog(true);
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/v1/settings/roles/${roleId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadRoles();
    } catch (error: any) {
      console.error('Failed to delete role:', error);
      alert(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleOpenAssignRole = (user: AdminUser) => {
    setSelectedUser(user);
    setSelectedRoleId(userRoles[user.id] || '');
    setAssignRoleDialog(true);
  };

  const handleAssignRoles = async () => {
    if (!selectedUser || !selectedRoleId) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v1/settings/users/${selectedUser.id}/roles`,
        { role_id: selectedRoleId },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      await loadAdminUsers();
      setAssignRoleDialog(false);
      setSelectedUser(null);
      setSelectedRoleId('');
    } catch (error) {
      console.error('Failed to assign role:', error);
    }
  };

  const actionTypes = ['create', 'read', 'update', 'delete', 'approve', 'export', 'import'];
  const resourceTypes = [
    'user',
    'alumni',
    'news',
    'event',
    'report',
    'setting',
    'line_bot',
    'consent',
    'activity_log',
    'behavior_event',
    'upload',
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">RBAC Management</Typography>
      </Box>

      <Card>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Permissions" />
          <Tab label="Roles" />
          <Tab label="User Assignments" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Permissions</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setPermissionDialog(true)}
            >
              Add Permission
            </Button>
          </Box>

          {/* Group permissions by resource */}
          {Object.entries(
            permissions.reduce((groups: { [key: string]: Permission[] }, perm) => {
              const resource = perm.resource;
              if (!groups[resource]) {
                groups[resource] = [];
              }
              groups[resource].push(perm);
              return groups;
            }, {})
          )
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([resource, resourcePermissions]) => (
              <Accordion key={resource} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip 
                      label={resource.toUpperCase()} 
                      color="primary" 
                      size="small" 
                    />
                    <Typography variant="body2" color="text.secondary">
                      {resourcePermissions.length} permission{resourcePermissions.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Actions</TableCell>
                          <TableCell>Scope</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell width="100">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resourcePermissions.map((perm, index) => (
                          <TableRow key={perm.id || `perm-${index}`}>
                            <TableCell>
                              {perm.actions.map((action, actionIndex) => (
                                <Chip 
                                  key={`${perm.id}-${action}-${actionIndex}`} 
                                  label={action} 
                                  size="small" 
                                  sx={{ mr: 0.5, mb: 0.5 }} 
                                  variant="outlined"
                                />
                              ))}
                            </TableCell>
                            <TableCell>
                              <Chip label={perm.scope} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{perm.description}</TableCell>
                            <TableCell>
                              <Tooltip title="Edit">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditPermission(perm)}
                                    disabled={perm.is_system}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={perm.is_system ? "Cannot delete system permission" : "Delete"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeletePermission(perm.id)}
                                    disabled={perm.is_system}
                                    color="error"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6">Roles</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setRoleDialog(true)}
            >
              Add Role
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Permissions</TableCell>
                  <TableCell>System</TableCell>
                  <TableCell width="100">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role, index) => (
                  <TableRow key={role.id || `role-${index}`}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>{role.display_name}</TableCell>
                    <TableCell>{role.level}</TableCell>
                    <TableCell>{role.permission_ids.length} permissions</TableCell>
                    <TableCell>
                      {role.is_system && <Chip label="System" size="small" />}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleEditRole(role)}
                            disabled={role.is_system}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={role.is_system ? "Cannot delete system role" : "Delete"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteRole(role.id)}
                            disabled={role.is_system}
                            color="error"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6">User Role Assignments</Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Assigned Roles</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adminUsers.map((user) => {
                  const assignedRoleId = userRoles[user.id];
                  const assignedRole = roles.find(r => r.id === assignedRoleId);
                  
                  return (
                    <TableRow key={user.id}>
                      <TableCell>{user.first_name} {user.last_name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {assignedRole ? (
                          <Chip 
                            key={assignedRole.id} 
                            label={assignedRole.display_name} 
                            size="small" 
                            sx={{ mr: 0.5 }} 
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">No role assigned</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenAssignRole(user)}
                        >
                          Assign Role
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Permission Dialog */}
      <Dialog 
        open={permissionDialog} 
        onClose={() => {
          setPermissionDialog(false);
          setEditingPermission(null);
          setNewPermission({ resource: '', actions: [], scope: 'all', description: '' });
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>{editingPermission ? 'Edit Permission' : 'Create Permission'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Resource Type</InputLabel>
                <Select
                  value={newPermission.resource}
                  onChange={(e) => setNewPermission({ ...newPermission, resource: e.target.value })}
                  label="Resource Type"
                >
                  {resourceTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Actions
              </Typography>
              <Box>
                {actionTypes.map((action) => (
                  <FormControlLabel
                    key={action}
                    control={
                      <Checkbox
                        checked={newPermission.actions.includes(action)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewPermission({
                              ...newPermission,
                              actions: [...newPermission.actions, action],
                            });
                          } else {
                            setNewPermission({
                              ...newPermission,
                              actions: newPermission.actions.filter((a) => a !== action),
                            });
                          }
                        }}
                      />
                    }
                    label={action}
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Scope</InputLabel>
                <Select
                  value={newPermission.scope}
                  onChange={(e) => setNewPermission({ ...newPermission, scope: e.target.value })}
                  label="Scope"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="own">Own</MenuItem>
                  <MenuItem value="department">Department</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={newPermission.description}
                onChange={(e) => setNewPermission({ ...newPermission, description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setPermissionDialog(false);
            setEditingPermission(null);
            setNewPermission({ resource: '', actions: [], scope: 'all', description: '' });
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePermission}>
            {editingPermission ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Dialog */}
      <Dialog 
        open={roleDialog} 
        onClose={() => {
          setRoleDialog(false);
          setEditingRole(null);
          setNewRole({ name: '', display_name: '', level: 1, permission_ids: [] });
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{editingRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Role Name"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Name"
                value={newRole.display_name}
                onChange={(e) => setNewRole({ ...newRole, display_name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Level"
                value={newRole.level}
                onChange={(e) => setNewRole({ ...newRole, level: parseInt(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Assign Permissions
              </Typography>
              <List>
                {permissions.map((perm, index) => (
                  <ListItem key={perm.id || `perm-dialog-${index}`}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newRole.permission_ids.includes(perm.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewRole({
                                ...newRole,
                                permission_ids: [...newRole.permission_ids, perm.id],
                              });
                            } else {
                              setNewRole({
                                ...newRole,
                                permission_ids: newRole.permission_ids.filter((id) => id !== perm.id),
                              });
                            }
                          }}
                        />
                      }
                      label={`${perm.resource} - ${perm.actions.join(', ')}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setRoleDialog(false);
            setEditingRole(null);
            setNewRole({ name: '', display_name: '', level: 1, permission_ids: [] });
          }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateRole}>
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={assignRoleDialog} onClose={() => setAssignRoleDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Role to {selectedUser?.first_name} {selectedUser?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select a Role
            </Typography>
            <RadioGroup
              value={selectedRoleId}
              onChange={(e) => setSelectedRoleId(e.target.value)}
            >
              {roles.map((role, index) => {
                // Handle both string and object IDs from backend
                const roleIdStr = role.id ? (typeof role.id === 'object' ? (role.id as any).$oid || String(role.id) : String(role.id)) : `temp-${index}`;
                return (
                  <FormControlLabel
                    key={roleIdStr}
                    value={roleIdStr}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">{role.display_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {role.name} (Level {role.level}) - {role.permission_ids.length} permissions
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignRoleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssignRoles}>
            Assign Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RBACManagement;
